// Feedback #829 (Hestia, 2026-09-17) — calendar event History.
//
// "…including the MM.DD.YY time/date, 'Created By,' and 'Changed By.'"
//
// Pure helpers for the event detail popup.  Kept out of Calendar.js so the
// date formatting — the part most likely to be wrong from Taiwan — is unit
// tested under TZ=Asia/Taipei like the rest of utils/eventTime.
import { format } from 'date-fns'
import { toFirmWallClock } from './eventTime'

/**
 * A DB timestamp (created_at / updated_at / audit changed_at — all naive UTC
 * from Postgres) rendered in the FIRM's timezone as "MM.DD.YY h:mm a",
 * exactly the format Hestia asked for.  '' for a missing/invalid value.
 */
export function formatHistoryStamp (value, tz) {
  const wall = toFirmWallClock(value, tz)
  if (!wall || Number.isNaN(wall.getTime())) return ''
  return format(wall, 'MM.dd.yy h:mm a')
}

/** Human labels for the audit field names GET /api/calendar/:id/history returns. */
export const EVENT_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  notes: 'Notes',
  start_time: 'Start',
  end_time: 'End',
  due_date: 'Due date',
  event_type: 'Type',
  event_category: 'Category',
  location: 'Location',
  visibility: 'Visibility',
  visible_to: 'Shared with',
  priority: 'Priority',
  matter_id: 'Matter',
  is_completed: 'Completed',
  completed_at: 'Completed at',
  completed_by: 'Completed by',
  completion_note: 'Completion note',
  sync_to_google: 'Google sync',
  push_to_user_ids: 'Google sync users',
  series_id: 'Series',
  reminders: 'Reminders',
  // Amicus report parity: attendees (from Amicus) and appointment / to-do.
  // Source and raw type id collapse into the same label as their value.
  attendee_user_ids: 'Attendees',
  attendees_source: 'Attendees',
  amicus_event_kind: 'Appointment / To-Do',
  amicus_event_type_id: 'Appointment / To-Do',
  // An event typed in Omnia linked to the same event in Amicus (EVT_AMICUS_LINK).
  amicus_id: 'Amicus link',
}

/**
 * Field names -> a short, de-duplicated, human list.  The completion fields
 * collapse to one "Completed" so a Complete click reads as one change, not
 * four.  Unknown names fall through prettified rather than being dropped.
 */
export function describeChangedFields (fields) {
  if (!Array.isArray(fields) || fields.length === 0) return ''
  const labels = []
  for (const f of fields) {
    const key = ['completed_at', 'completed_by'].includes(f) ? 'is_completed' : f
    const label = EVENT_FIELD_LABELS[key] ||
      String(key).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
    if (!labels.includes(label)) labels.push(label)
  }
  return labels.join(', ')
}

/**
 * The "Created …" line.
 *
 * Events imported from Amicus (amicus_id set — 11,873 of them) are NOT
 * creation records: their created_at is the IMPORT timestamp, and created_by
 * is the Amicus author mapped to a user OR, when that mapping missed, whoever
 * ran the import (migration.js: `peopleToUserMap[row.AuthorId] || userId`).
 * Rendering "Created <import date> by <name>" on a 2019 hearing would invent
 * a creation record.  They get "Imported from Amicus <date>" — no name, and
 * not the word "Created".
 *
 * A native event with no creator (user since deleted) shows the date only.
 *
 * An event typed in Omnia and later linked to the same event in Amicus (James
 * 2026-10-27: the import links instead of adding a copy) carries an amicus_id
 * but WAS created in Omnia, by that person: the server marks it
 * `amicus_linked: true` (api/src/lib/amicusEventLink.js attachAmicusLinked)
 * and it keeps its "Created … by" line.
 */
export function createdLine (event, tz) {
  if (!event) return ''
  const when = formatHistoryStamp(event.created_at, tz)
  if (!when) return ''
  if (event.amicus_id && event.amicus_linked !== true) return `Imported from Amicus ${when.split(' ')[0]}`
  return event.created_by_name ? `Created ${when} by ${event.created_by_name}` : `Created ${when}`
}

/**
 * Contract C3 (code review of 9ff73f1, finding #6) — the exact words for a
 * History entry the SYSTEM wrote, today only the EVT_RETYPE rollup for the
 * Amicus events whose type the importer had merged into another.  Kept here
 * so the wording has one home; components/EventHistory.js decides WHEN to use
 * it.
 *
 * It decides by the entry's action_code, NOT by user_name being null.
 * audit_log.user_id is ON DELETE SET NULL, so every edit by a staff member
 * whose account was later permanently deleted also comes back with
 * user_name: null — and reads "Former user", not Omnia.  The server's
 * 'Former user' label only covers a deleted user whose id survived on the
 * row, which that FK makes impossible.  (This file used to export a
 * historyActorName() that treated every null as Omnia and said the opposite;
 * it rendered nowhere, so it was removed rather than left to drift from the
 * rule that actually renders — code review of the finding #6 fix.)
 */
export const AUTOMATIC_CORRECTION_ACTOR = 'Omnia (automatic correction)'

/**
 * The "Last changed …" line — shown ONLY when the app recorded who made the
 * change (events.updated_by).  Events edited before history recording began
 * still have an updated_at, but nobody knows who moved it; showing that date
 * with no name would read as a change by someone, which the firm cannot back
 * up.  '' means "do not render the line".
 *
 * A missing name is deliberately NOT rendered as AUTOMATIC_CORRECTION_ACTOR
 * here.  The event row carries no field that says WHY updated_by is NULL, and
 * on production (measured 2026-09-26) 11,926 of 11,928 events have it NULL —
 * 9,757 with an updated_at later than their created_at, and every newly
 * created event too.  "by Omnia (automatic correction)" on those would be
 * false.  An automatic correction is shown in the History list instead, where
 * the audit row's own action code proves it (components/EventHistory.js).
 *
 * This is a deliberate reading of contract C3, whose last sentence as first
 * written would have put the automatic actor on this line too; the
 * adversarial review of the finding #6 fix agreed the line must be omitted.
 */
export function lastChangedLine (event, tz) {
  const name = event && typeof event.updated_by_name === 'string' ? event.updated_by_name.trim() : ''
  if (!name) return ''
  const when = formatHistoryStamp(event.updated_at, tz)
  if (!when) return ''
  return `Last changed ${when} by ${name}`
}

/**
 * Feedback #829 follow-up — the matter view's event list comes from the
 * matter bundle (GET /api/matters/:id), whose events query does not join the
 * author names.  The names are fetched once from GET /api/calendar/matter/:id
 * and merged in by id.
 *
 * True when at least one event is missing the author fields entirely (the
 * key is absent — an explicit null means "known, nobody", e.g. a never-edited
 * event, and needs no fetch).  Once the bundle carries the names this goes
 * false and the extra request stops by itself.
 */
export function eventsNeedAuthorNames (events) {
  if (!Array.isArray(events) || events.length === 0) return false
  return events.some(e => e && !Object.prototype.hasOwnProperty.call(e, 'created_by_name'))
}

/**
 * Merge created_by_name / updated_by_name from `rows` into `events` by id.
 *
 * Only NAMES are merged, and only onto events already in `events`: a row the
 * caller's list does not contain is ignored, so the merge can never add an
 * event to the list.  An event's own value wins when it already has the key.
 * updated_by_name is merged only when the row's updated_at matches the
 * event's — a stale row must not name the previous editor of a newer edit.
 * Returns a new array; the inputs are not mutated.
 */
export function mergeEventAuthors (events, rows) {
  if (!Array.isArray(events)) return []
  if (!Array.isArray(rows) || rows.length === 0) return events
  const byId = new Map()
  for (const r of rows) {
    if (r && r.id != null) byId.set(r.id, r)
  }
  return events.map(e => {
    const r = e && byId.get(e.id)
    if (!r) return e
    const out = { ...e }
    if (!Object.prototype.hasOwnProperty.call(e, 'created_by_name')) {
      out.created_by_name = r.created_by_name ?? null
    }
    // Typed in Omnia, linked to Amicus: the matter bundle doesn't carry the
    // flag, GET /api/calendar/matter/:id does (createdLine reads it).
    if (!Object.prototype.hasOwnProperty.call(e, 'amicus_linked') &&
        Object.prototype.hasOwnProperty.call(r, 'amicus_linked')) {
      out.amicus_linked = r.amicus_linked === true
    }
    // The editor's name is only true for the edit it was fetched with.  The
    // matter bundle reloads right after an edit, so `e.updated_at` moves on
    // while `rows` still holds the names fetched before it.  Pairing the NEW
    // stamp with the OLD name would read "Last changed <just now> by <the
    // previous editor>".  Merge it only when both describe the same edit;
    // otherwise leave the key absent, so lastChangedLine renders nothing
    // until the refetch (keyed on updated_at) brings the right name.
    if (!Object.prototype.hasOwnProperty.call(e, 'updated_by_name') &&
        sameStamp(e.updated_at, r.updated_at)) {
      out.updated_by_name = r.updated_by_name ?? null
    }
    return out
  })
}

// Two DB timestamps as they arrive over JSON.  Compared by instant when both
// parse (so a format difference between endpoints cannot fake a mismatch),
// otherwise by value; both absent counts as the same.
function sameStamp (a, b) {
  const x = a ?? null
  const y = b ?? null
  if (x === null || y === null) return x === y
  const tx = Date.parse(x)
  const ty = Date.parse(y)
  if (!Number.isNaN(tx) && !Number.isNaN(ty)) return tx === ty
  return String(x) === String(y)
}
